"use client";

import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type CollageItem = {
  src: string;
  alt: string;
  widthClassName: string;
};

export default function ProjectImageCollage({
  items,
}: {
  items: readonly CollageItem[];
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isViewerOpen = selectedIndex !== null;
  const selectedItem = selectedIndex === null ? null : items[selectedIndex];

  useEffect(() => {
    if (!isViewerOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSelectedIndex(null);
      } else if (event.key === "ArrowLeft") {
        setSelectedIndex((current) =>
          current === null ? null : (current - 1 + items.length) % items.length,
        );
      } else if (event.key === "ArrowRight") {
        setSelectedIndex((current) =>
          current === null ? null : (current + 1) % items.length,
        );
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isViewerOpen, items.length]);

  function showPrevious() {
    setSelectedIndex((current) =>
      current === null ? null : (current - 1 + items.length) % items.length,
    );
  }

  function showNext() {
    setSelectedIndex((current) =>
      current === null ? null : (current + 1) % items.length,
    );
  }

  return (
    <>
      <section
        aria-label="Project image collage"
        className="bg-sand px-6 py-16 sm:px-8 sm:py-24 lg:py-28"
      >
        <div className="mx-auto grid max-w-[82.5rem] grid-cols-2 gap-1 overflow-hidden border-4 border-burgundy bg-burgundy md:grid-cols-4 lg:grid-cols-[repeat(24,minmax(0,1fr))] lg:grid-rows-2">
          {items.map(({ src, alt, widthClassName }, index) => (
            <button
              key={src}
              type="button"
              onClick={() => setSelectedIndex(index)}
              aria-label={`Open ${alt}`}
              className={`group relative aspect-square cursor-zoom-in overflow-hidden focus-visible:outline-4 focus-visible:outline-offset-[-4px] focus-visible:outline-sky lg:aspect-auto lg:min-h-72 ${widthClassName}`}
            >
              <Image
                src={src}
                alt={alt}
                fill
                sizes="(min-width: 1024px) 33vw, 50vw"
                className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </button>
          ))}
        </div>
      </section>

      {selectedItem && selectedIndex !== null
        ? createPortal(
          <div
          role="dialog"
          aria-modal="true"
          aria-label="Project image viewer"
          className="fixed inset-0 z-[100] flex h-dvh flex-col bg-sand text-earth"
        >
          <header className="flex shrink-0 items-center justify-between px-4 py-3 sm:px-6">
            <p className="text-xs font-bold tracking-[0.18em] uppercase">
              {selectedIndex + 1} / {items.length}
            </p>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => setSelectedIndex(null)}
              aria-label="Close image viewer"
              className="grid size-11 place-items-center transition-colors hover:bg-earth hover:text-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burgundy"
            >
              <X aria-hidden="true" />
            </button>
          </header>

          <div className="flex min-h-0 flex-1 items-stretch">
            <button
              type="button"
              onClick={showPrevious}
              aria-label="View previous image"
              className="hidden w-20 shrink-0 place-items-center transition-colors hover:bg-earth/10 focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-burgundy sm:grid"
            >
              <ChevronLeft size={34} aria-hidden="true" />
            </button>

            <div className="relative min-w-0 flex-1">
              <Image
                key={selectedItem.src}
                src={selectedItem.src}
                alt={selectedItem.alt}
                fill
                sizes="100vw"
                className="object-contain p-4 sm:p-8"
                priority
              />

              <div className="absolute inset-x-3 top-1/2 flex -translate-y-1/2 justify-between sm:hidden">
                <button
                  type="button"
                  onClick={showPrevious}
                  aria-label="View previous image"
                  className="grid size-11 place-items-center bg-earth/85 text-sand focus-visible:outline-2 focus-visible:outline-sky"
                >
                  <ChevronLeft aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={showNext}
                  aria-label="View next image"
                  className="grid size-11 place-items-center bg-earth/85 text-sand focus-visible:outline-2 focus-visible:outline-sky"
                >
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={showNext}
              aria-label="View next image"
              className="hidden w-20 shrink-0 place-items-center transition-colors hover:bg-earth/10 focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-burgundy sm:grid"
            >
              <ChevronRight size={34} aria-hidden="true" />
            </button>
          </div>

          <div className="shrink-0 overflow-x-auto px-4 py-3 sm:px-6">
            <div className="mx-auto flex w-max gap-2">
              {items.map((item, index) => (
                <button
                  key={item.src}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  aria-label={`View image ${index + 1}`}
                  aria-current={index === selectedIndex ? "true" : undefined}
                  className={`relative h-14 w-16 shrink-0 overflow-hidden transition-opacity sm:h-16 sm:w-20 ${
                    index === selectedIndex
                      ? "opacity-100"
                      : "opacity-50 hover:opacity-100"
                  }`}
                >
                  <Image
                    src={item.src}
                    alt=""
                    fill
                    sizes="80px"
                    className="object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
          </div>,
          document.body,
        )
        : null}
    </>
  );
}